import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { UPLOAD_BASE, fmtDateTH, fmtDateTimeTH } from './aoShared'

// หนังสือเวียน/เกษียนถึงฉัน — ครูทุกคนเข้าถึงได้ กด "รับทราบ" เพื่อยืนยัน
export default function MyReceivedDocs() {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/ao/my/received-docs')
      if (res.data.success) setDocs(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleAck = async (d) => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันรับทราบหนังสือ?',
      text: `${d.in_full_no} — ${d.in_subject}`,
      input: 'text',
      inputPlaceholder: 'หมายเหตุ (ไม่บังคับ)',
      showCancelButton: true,
      confirmButtonText: 'รับทราบ',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.post(`/api/ao/my/received-docs/${d.rt_id}/ack`, { note: result.value || '' })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'รับทราบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const pending = docs.filter(d => !d.rt_ack)
  const acked = docs.filter(d => d.rt_ack)

  const DocCard = ({ d }) => (
    <div className="border rounded p-3 h-100 d-flex flex-column">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <span className="fw-semibold">{d.in_subject}</span>
        </div>
        {d.rt_ack
          ? <Badge bg="success">รับทราบแล้ว</Badge>
          : <Badge bg="warning">รอรับทราบ</Badge>}
      </div>
      <div className="mt-2 pt-2 border-top fs-sm flex-grow-1">
        <div><span className="text-muted">เลขรับ:</span> {d.in_full_no} · {fmtDateTH(d.in_received_date)}</div>
        <div><span className="text-muted">จาก:</span> {d.in_source}</div>
        {d.rt_note && <div><span className="text-muted">ข้อความเกษียน:</span> {d.rt_note}</div>}
        {d.forwarded_by_name && <div><span className="text-muted">ส่งต่อโดย:</span> {d.forwarded_by_name}</div>}
        {d.rt_ack === 1 && (
          <div className="text-muted fs-xs mt-1">รับทราบเมื่อ {fmtDateTimeTH(d.rt_ack_at)}{d.rt_ack_note ? ` — ${d.rt_ack_note}` : ''}</div>
        )}
      </div>
      <div className="mt-2 pt-2 border-top d-flex gap-2">
        {d.in_file && (
          <Button size="sm" variant="outline-secondary" className="flex-grow-1"
            href={`${UPLOAD_BASE}/uploads/ao_docs/${d.in_file}`} target="_blank" rel="noreferrer">
            <i className="ri-attachment-2 me-1" />เปิดไฟล์
          </Button>
        )}
        {!d.rt_ack && (
          <Button size="sm" variant="primary" className="flex-grow-1" onClick={() => handleAck(d)}>
            <i className="ri-check-line me-1" />รับทราบ
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-mail-open-line me-2 text-primary" />
              หนังสือเวียนถึงฉัน
            </h4>
            {pending.length > 0 && <Badge bg="warning" className="fs-sm">รอรับทราบ {pending.length} ฉบับ</Badge>}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : docs.length === 0 ? (
            <Card className="card-one"><Card.Body className="text-center text-muted py-5">ยังไม่มีหนังสือเวียนถึงคุณ</Card.Body></Card>
          ) : (
            <>
              {pending.length > 0 && (
                <>
                  <h6 className="mb-2">รอรับทราบ</h6>
                  <Row className="g-3 mb-4">
                    {pending.map(d => <Col key={d.rt_id} xs={12} md={6} xl={4}><DocCard d={d} /></Col>)}
                  </Row>
                </>
              )}
              {acked.length > 0 && (
                <>
                  <h6 className="mb-2 text-muted">รับทราบแล้ว</h6>
                  <Row className="g-3">
                    {acked.map(d => <Col key={d.rt_id} xs={12} md={6} xl={4}><DocCard d={d} /></Col>)}
                  </Row>
                </>
              )}
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
